import { getAPIHost, getNestAPIHost, processKeys } from '@/helpers/api';
import { RTK_CACHE_LIFE } from '@/helpers/constants/common';
import type {
  HelpArticalListsPageType,
  IHelpArticleListsResponse,
} from '@/helpers/types/helpCenterTypes';
import type { IHelpArticle } from '@/helpers/types/xcmsTypes';
import { xcoverAPI } from '..';

interface IGetHelpArticleListsProps {
  helpType: HelpArticalListsPageType;
  slug: string;
  locale: string;
  auth: boolean;
}

interface IGetHelpArticleListsResponse {
  helpPageContent: IHelpArticleListsResponse;
  partner: {
    slug: string;
    title: string;
    logo: string;
    signupMethodOnXcover: string | null;
  };
}

interface IGetHelpArticleProps {
  partner?: string | null;
  slug: string;
  locale: string;
  revisionId?: string | null;
  auth: boolean;
}

export const helpAPI = xcoverAPI.injectEndpoints({
  endpoints: (builder) => ({
    getHelpArticleListsContent: builder.query<
      IGetHelpArticleListsResponse,
      IGetHelpArticleListsProps
    >({
      query: ({ helpType, slug, locale, auth }) => {
        const searchParams = new URLSearchParams({
          language: locale,
          isLoggedIn: auth.toString(),
        });

        const url = `${getAPIHost({ version: 'v2' })}/help/${helpType}/${slug}/?${searchParams}`;

        if (!auth) {
          return {
            url,
            headers: { auth: 'false' },
          };
        }

        return url;
      },
      transformResponse: (response: any) => processKeys(response),
      keepUnusedDataFor: RTK_CACHE_LIFE,
    }),
    getHelpArticle: builder.query<IHelpArticle, IGetHelpArticleProps>({
      query: ({ slug, partner, locale, revisionId, auth }) => {
        const searchParams = new URLSearchParams({
          language: locale,
          isLoggedIn: auth.toString(),
          ...(partner && { partner }),
          ...(revisionId && { revision_id: revisionId }),
        });

        const url = `${getNestAPIHost()}/help/articles/${slug}/?${searchParams}`;

        if (!auth) {
          return {
            url,
            headers: { auth: 'false' },
          };
        }

        return url;
      },
      transformResponse: (response: any) => processKeys(response),
      keepUnusedDataFor: RTK_CACHE_LIFE,
    }),
  }),
});

export const { useGetHelpArticleListsContentQuery, useGetHelpArticleQuery } =
  helpAPI;

export const { getHelpArticleListsContent, getHelpArticle } = helpAPI.endpoints;
export const clearHelpArticle = () => helpAPI.util.resetApiState();
